import { PrintApiPrinter, PrintApiQueryResponse, PrintApiPrinterQueryParams, PrintApiJob, PrintApiServiceConfig, PrintApiJobUpdateParams, PrintApiSyncParams, PrintApiSyncResult } from "./types";

/**
 * Singleton client for Print API that manages API calls to the /printers endpoint.
 * This ensures that multiple services (label/receipt) share the same configuration.
 */
export class PrintApiClient {
  private static instance: PrintApiClient;
  private config: PrintApiServiceConfig;

  // Supports making a single call to the worker api for both label and receipt printers
  // and filters results in the service implementation
  private getPrintersPromise: Promise<PrintApiPrinter[]> | undefined;

  private constructor(config: PrintApiServiceConfig) {
    this.config = config;
  }

  /**
   * Get or create the singleton instance
   */
  public static getInstance(config: { baseUrl: string; locationIdentifier: string }): PrintApiClient {
    if (!PrintApiClient.instance) {
      PrintApiClient.instance = new PrintApiClient(config);
    } else if (PrintApiClient.instance.config.locationIdentifier !== config.locationIdentifier) {
      PrintApiClient.instance.getPrintersPromise = undefined;
      PrintApiClient.instance.config = { ...PrintApiClient.instance.config, ...config };
    }
    return PrintApiClient.instance;
  }

  /**
   * Update the configuration (useful for testing or runtime config changes)
   */
  public updateConfig(config: Partial<PrintApiServiceConfig>): void {
    this.config = { ...this.config, ...config };
    this.getPrintersPromise = undefined;
  }

  get locationIdentifier(): string {
    return this.config.locationIdentifier;
  }

  public async syncPrinters(params: PrintApiSyncParams): Promise<PrintApiSyncResult> {
    const url = new URL(`${this.config.baseUrl}/printers/sync`);
    url.searchParams.set('lid', this.config.locationIdentifier);

    const response = await fetch(url.toString(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      throw new Error(`Failed to sync printers: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  public async updateJobStatus(job: PrintApiJob, update: PrintApiJobUpdateParams): Promise<PrintApiJob> {
    const url = new URL(`${this.config.baseUrl}/jobs/${job.id}`);
    url.searchParams.set('lid', this.config.locationIdentifier);

    const response = await fetch(url.toString(), {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(update),
    });

    return response.json() as Promise<PrintApiJob>;
  }

  /**
   * Fetch printers from the API with pagination support.
   * Makes a fresh API call each time. The library handles caching at a higher level.
   */
  public async getPrinters(config: PrintApiPrinterQueryParams): Promise<PrintApiQueryResponse<PrintApiPrinter>> {
    try {
      const url = new URL(`${this.config.baseUrl}/printers`);
      url.searchParams.set('lid', this.config.locationIdentifier);

      if (config.connectionType) {
        url.searchParams.set('connectionType', config.connectionType);
      }
      if (config.enabled) {
        url.searchParams.set('enabled', config.enabled.toString());
      }
      if (config.implementationType) {
        url.searchParams.set('implementationType', config.implementationType);
      }
      if (config.language) {
        url.searchParams.set('language', config.language);
      }
      if (config.name) {
        url.searchParams.set('name', config.name);
      }
      if (config.macAddress) {
        url.searchParams.set('macAddress', config.macAddress);
      }
      if (config.sourceId) {
        url.searchParams.set('sourceId', config.sourceId.toString());
      }
      if (config.type) {
        url.searchParams.set('type', config.type);
      }

      // add paging params
      if (config.page) {
        url.searchParams.set('page', config.page.toString());
      }
      if (config.limit) {
        url.searchParams.set('limit', config.limit.toString());
      }
      if (config.sortBy) {
        url.searchParams.set('sortBy', config.sortBy);
      }
      if (config.sortOrder) {
        url.searchParams.set('sortOrder', config.sortOrder);
      }
      

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`API request failed: ${response.status} ${response.statusText}`);
      }
      
      const data: PrintApiQueryResponse<PrintApiPrinter> = await response.json();
      return data;
    } catch (error) {
      console.error('Failed to fetch printers from API:', error);
      // Re-throw the error so services can handle it appropriately
      throw error;
    }
  }

  /**
   * Fetch ALL printers from the API by automatically handling pagination.
   * This method will make multiple API calls if necessary to retrieve all results.
   */
  public async getAllPrinters(): Promise<PrintApiPrinter[]> {
    try {
      if (this.getPrintersPromise) {
        return this.getPrintersPromise;
      }

      this.getPrintersPromise = (async () => {
        const allPrinters: PrintApiPrinter[] = [];
        let currentPage = 1;
        let totalPages = 1;
        const limit = 500;

        do {
          const response = await this.getPrinters({ page: currentPage, limit });
          allPrinters.push(...response.items);
          totalPages = response.totalPages;
          currentPage++;
        } while (currentPage <= totalPages);

        return allPrinters;
      })();

      return await this.getPrintersPromise;
    } catch (error) {
      throw error;
    } finally {
      this.getPrintersPromise = undefined;
    }
  }
}
 
